<template>
  <div
    class="platform-grid"
    :aria-label="ariaLabel"
    :style="gridStyle"
  >
    <figure
      v-for="item in items"
      :key="item.key"
      class="platform-grid__card"
    >
      <img
        class="platform-grid__media"
        :src="item.image"
        :alt="item.alt || item.imageAlt || item.label || item.title || ''"
        loading="lazy"
      />
      <figcaption v-if="showLabel && (item.label || item.title)" class="platform-grid__label">
        {{ item.label || item.title }}
      </figcaption>
    </figure>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface PlatformGridItem {
  key: string
  image: string
  label?: string
  title?: string
  alt?: string
  imageAlt?: string
}

const props = withDefaults(defineProps<{
  items: PlatformGridItem[]
  ariaLabel?: string
  desktopColumns?: number
  tabletColumns?: number
  mobileColumns?: number
  gap?: string
  cardRadius?: string
  cardAspectRatio?: string
  showLabel?: boolean
  labelFontSize?: string
  labelLineHeight?: string
  labelInsetX?: string
  labelInsetBottom?: string
  overlay?: string
}>(), {
  ariaLabel: '',
  desktopColumns: 3,
  tabletColumns: 2,
  mobileColumns: 1,
  gap: '24px',
  cardRadius: '30px',
  cardAspectRatio: '584 / 383',
  showLabel: true,
  labelFontSize: 'var(--pixso-fs-36)',
  labelLineHeight: 'var(--pixso-lh-36)',
  labelInsetX: '24px',
  labelInsetBottom: '24px',
  overlay: 'linear-gradient(180deg, rgba(0, 0, 0, 0.02) 34%, rgba(0, 0, 0, 0.72) 100%)'
})

const gridStyle = computed(() => ({
  '--platform-grid-columns-desktop': String(props.desktopColumns),
  '--platform-grid-columns-tablet': String(props.tabletColumns),
  '--platform-grid-columns-mobile': String(props.mobileColumns),
  '--platform-grid-gap': props.gap,
  '--platform-grid-card-radius': props.cardRadius,
  '--platform-grid-card-aspect-ratio': props.cardAspectRatio,
  '--platform-grid-label-display': props.showLabel ? 'block' : 'none',
  '--platform-grid-label-font-size': props.labelFontSize,
  '--platform-grid-label-line-height': props.labelLineHeight,
  '--platform-grid-label-inset-x': props.labelInsetX,
  '--platform-grid-label-inset-bottom': props.labelInsetBottom,
  '--platform-grid-overlay': props.overlay
}))
</script>

<style scoped>
.platform-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(var(--platform-grid-columns-desktop), minmax(0, 1fr));
  gap: var(--platform-grid-gap);
}

.platform-grid__card {
  position: relative;
  margin: 0;
  aspect-ratio: var(--platform-grid-card-aspect-ratio);
  overflow: hidden;
  border-radius: var(--platform-grid-card-radius);
  background: var(--pixso-color-surface);
  isolation: isolate;
}

.platform-grid__card::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  background: var(--platform-grid-overlay);
  pointer-events: none;
}

.platform-grid__media {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.platform-grid__label {
  display: var(--platform-grid-label-display);
  position: absolute;
  left: var(--platform-grid-label-inset-x);
  right: var(--platform-grid-label-inset-x);
  bottom: var(--platform-grid-label-inset-bottom);
  z-index: 2;
  margin: 0;
  color: var(--pixso-color-text);
  font-family: var(--pixso-font-cn);
  font-size: var(--pixso-fs-36);
  line-height: var(--pixso-lh-36);
  font-weight: 700;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.68);
}

@media (max-width: 1200px) {
  .platform-grid {
    grid-template-columns: repeat(var(--platform-grid-columns-tablet), minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .platform-grid {
    grid-template-columns: repeat(var(--platform-grid-columns-mobile), minmax(0, 1fr));
  }

  .platform-grid__label {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }
}
</style>
